// Auto-merge for offline-vs-server conflicts on a list's prose
// fields (content + title). Replaces the old "keep mine / theirs /
// both" modal — Walter 2026-05-04: the modal was friction without
// payoff, and "keep both" creating a duplicate list was confusing.
//
// Strategy (Walter's A+C combined):
//   • TRIVIAL case (whitespace / newline noise / one is empty / one
//     is a strict subset): auto-resolve silently. No marker, no
//     banner. (Option C.)
//   • SUBSTANTIVE case: line-aware merge that PREPENDS the offline-
//     only lines to the top of the server content with a marker
//     comment. Walter's lists are top-down (newest at top), so the
//     conflicting edits land where he'll see them. (Option A,
//     prepend variant.)
//
// Output also flags `hadConflict` so the caller can surface the
// "conflicting edits at top — review & dismiss" banner in the
// affected list's chrome.

// Markers are detectable by the in-list banner. Keep them stable —
// changing these breaks banner detection on existing lists.
export const CONFLICT_HEADER = '// 🔀 CONFLICTING EDITS — these were made offline while another version existed';
export const CONFLICT_DIVIDER = '// ↑ from offline (this device) — ↓ original (synced) ↓';

// Identifies a list whose content currently has the merge marker
// at the top. Used by the banner to render itself + the "dismiss"
// affordance.
export function hasConflictMarker(content) {
  if (!content) return false;
  return content.startsWith(CONFLICT_HEADER);
}

// Strip the conflict block from content. Removes everything from
// CONFLICT_HEADER through CONFLICT_DIVIDER (inclusive) and the
// blank line after it. Used by the banner's "discard offline edits"
// button when the user has reviewed the offline edits and wants to
// drop them in favor of the cloud version.
//
// Walter 2026-05-17: previously labelled "dismiss" — confusing
// because users didn't realise it would drop their offline edits.
// Now paired with stripConflictMarkerKeepBoth (below) and surfaced
// behind explicit button labels.
export function stripConflictMarker(content) {
  if (!hasConflictMarker(content)) return content;
  const lines = content.split('\n');
  // Find the divider line.
  const dividerIdx = lines.indexOf(CONFLICT_DIVIDER);
  if (dividerIdx === -1) {
    // Malformed — header without divider. Just remove the header
    // line by itself; user can clean the rest manually.
    return lines.slice(1).join('\n');
  }
  // Skip header + offline-only lines + divider + ONE blank line
  // after divider (we always insert exactly one).
  let after = dividerIdx + 1;
  if (lines[after] === '') after += 1;
  return lines.slice(after).join('\n');
}

// Walter 2026-05-17: "keep both" resolution — removes ONLY the
// header and divider lines, preserving every line of content (both
// the offline-only block at top AND the cloud content below). This
// is the default action: most users want their offline edits kept,
// and "dismiss" silently discarded them. Now the band makes the
// choice explicit.
export function stripConflictMarkerKeepBoth(content) {
  if (!hasConflictMarker(content)) return content;
  const lines = content.split('\n');
  const dividerIdx = lines.indexOf(CONFLICT_DIVIDER);
  if (dividerIdx === -1) {
    // Malformed — header without divider. Drop just the header.
    return lines.slice(1).join('\n');
  }
  // Keep:
  //   lines[1 .. dividerIdx-1]  ← offline-only edits
  //   lines[dividerIdx+1 ..]    ← cloud content (incl. its leading blank)
  // Drop:
  //   lines[0]                  ← header
  //   lines[dividerIdx]         ← divider
  // We collapse the (now potentially blank) seam between the two
  // halves down to a single empty line so the join doesn't leave
  // visible whitespace ladders.
  const offline = lines.slice(1, dividerIdx);
  let afterIdx = dividerIdx + 1;
  if (lines[afterIdx] === '') afterIdx += 1;
  const cloud = lines.slice(afterIdx);
  const sep = (offline.length > 0 && cloud.length > 0) ? [''] : [];
  return [...offline, ...sep, ...cloud].join('\n');
}

// Normalize a string for trivial-equality comparison: trim trailing
// whitespace per line, collapse 3+ consecutive newlines down to 2,
// trim outer whitespace. Catches "user inserted/removed a trailing
// newline" and "user added some indentation that the server doesn't
// have" — those shouldn't trigger a conflict UI.
function normalizeForCompare(s) {
  return (s || '')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * mergeConflict(serverContent, offlineContent)
 *
 * Returns { merged, hadConflict }.
 *   - merged: the string to write to Firestore.
 *   - hadConflict: true if the caller should surface the banner.
 *
 * Trivial cases (auto-resolve, hadConflict=false):
 *   - exact match
 *   - whitespace-only differences
 *   - offline is empty (take server)
 *   - server is empty (take offline)
 *   - normalized offline == normalized server (whitespace noise only)
 *   - offline-trimmed-as-line-set is a SUBSET of server's (server
 *     already contains everything offline added; e.g. user typed it
 *     online on another device first)
 *
 * Substantive case (hadConflict=true):
 *   - Walter 2026-05-17 — we now PRESERVE OFFLINE POSITION. The
 *     prepend-marker approach (hoist offline-only lines to the top
 *     with `// 🔀 CONFLICTING EDITS` and `// ↑ from offline …`
 *     comments) was confusing: it rewrote the user's list out from
 *     under them, moved their offline edits away from where they
 *     typed them, and inserted comment noise. With the new history
 *     side-by-side modal + color-coded diff, the user can see
 *     exactly which lines differ between current and the cloud's
 *     pre-merge snapshot WITHOUT mangling the live content.
 *   - So the merged result is just the offline content as-is. The
 *     cloud's version goes to /history (caller's responsibility);
 *     the caller also sets `conflictPendingReview` on the list so
 *     the band can surface.
 */
export function mergeConflict(serverContent, offlineContent) {
  const server = serverContent ?? '';
  const offline = offlineContent ?? '';

  // Trivial: identical bytes.
  if (server === offline) return { merged: server, hadConflict: false };

  // Trivial: one side is empty.
  if (!offline.trim()) return { merged: server, hadConflict: false };
  if (!server.trim()) return { merged: offline, hadConflict: false };

  // Trivial: same modulo whitespace.
  if (normalizeForCompare(server) === normalizeForCompare(offline)) {
    return { merged: server, hadConflict: false };
  }

  // Line-set comparison. We compare TRIMMED lines so an indentation
  // tweak doesn't get flagged as new content.
  const serverLines = server.split('\n');
  const offlineLines = offline.split('\n');
  const serverTrimmedSet = new Set(
    serverLines.map(l => l.trim()).filter(l => l.length > 0),
  );

  // Lines present in offline but missing from server. We don't need
  // to keep them separately anymore (no prepend-marker), but the
  // count drives the "is it substantive?" decision below.
  const offlineOnly = offlineLines.filter(l => {
    const t = l.trim();
    return t.length > 0 && !serverTrimmedSet.has(t);
  });

  // Trivial: offline added nothing new. (User may have deleted some
  // lines or reordered, but every line they kept already exists on
  // the server.) Take server as-is.
  if (offlineOnly.length === 0) {
    return { merged: server, hadConflict: false };
  }

  // Substantive case (Walter 2026-05-17): write offline as-is. The
  // side-by-side history modal surfaces the cloud's pre-merge state
  // with color-coded diffs, so the user can see what's different
  // without us rewriting their list.
  return { merged: offline, hadConflict: true };
}

/**
 * mergeTitle — trivial conflict resolver for the title field. Single
 * string, no line semantics. We take the offline value if both differ
 * (the user just typed it; server's was likely older). Empty wins
 * lose to non-empty so we don't blank out a title.
 */
export function mergeTitle(serverTitle, offlineTitle) {
  const server = (serverTitle ?? '').toString();
  const offline = (offlineTitle ?? '').toString();
  if (server === offline) return server;
  if (!offline.trim()) return server;
  if (!server.trim()) return offline;
  // Both non-empty and different — take offline. The discarded
  // server title is recoverable from Firestore's update audit if
  // ever needed; not worth surfacing in the prose.
  return offline;
}
